import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import clsx from 'clsx';
import { ArrowRight, Camera, Check, Download, Eye, Globe, Palette, Play, Smartphone, Sparkles, Video } from 'lucide-react';
import { formatTSh, priceLabel, type Product } from '@tangazo/shared';
import { api } from '@/api/client';
import { useConfig, useTemplates } from '@/api/hooks';
import { TemplateCard } from '@/components/TemplateCard';
import { aiFeature } from '@/flow/PackageCard';
import type { Order } from '@/api/types';
import { useT } from '@/lib/i18n';
import { storage } from '@/lib/storage';
import { errText } from '@/lib/errors';
import { drawAd } from '@/lib/render';
import type { Format } from '@tangazo/shared';
import { loadImage } from '@/lib/ad';
import { ensureFonts } from '@/lib/fonts';

// Sample ads, drawn by the same templates customers get.
export const SAMPLES = [
  { n: 1, template: 'bango', accent: '#C2185B', photo: '/stock/shoes.jpg', price: '35000', location: 'Kariakoo, Dar es Salaam' },
  { n: 2, template: 'ofa', accent: '#F2A900', photo: '/stock/burger.jpg', price: '8000', location: 'Sinza, Dar es Salaam' },
  { n: 3, template: 'kanga', accent: '#1B998B', photo: '/stock/tailor.jpg', price: '', location: 'Mbezi, Dar es Salaam' },
  { n: 4, template: 'safi', accent: '#6A4C93', photo: '/stock/nails.jpg', price: '25000', location: 'Mikocheni' },
  { n: 5, template: 'usiku', accent: '#2E86DE', photo: '/stock/phone.jpg', price: '450000', location: 'Kariakoo' },
] as const;

export function SampleAd({ s, format, scale, className, accent }: { s: (typeof SAMPLES)[number]; format: Format; scale: number; className?: string; accent?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { t, lang } = useT();
  useEffect(() => {
    let live = true;
    Promise.all([ensureFonts(), loadImage(s.photo).catch(() => null)]).then(([, photo]) => {
      if (!live || !ref.current) return;
      drawAd(ref.current, {
        template: s.template, format, accent: accent ?? s.accent, scale,
        text: { headline: t(`land.s${s.n}_head`), subline: t(`land.s${s.n}_sub`), offer: t(`land.s${s.n}_offer`), cta: t('land.cta_word') },
        business: t(`land.s${s.n}_business`), location: s.location, price: priceLabel(s.price), phone: '0712 345 678',
        labels: { call: t('ad.call'), offerWord: t('ad.offer_word') }, photo,
      });
    });
    return () => {
      live = false;
    };
  }, [s, format, scale, t, lang, accent]);
  return <canvas ref={ref} className={className} role="img" aria-label={t('land.sample_label', { name: t(`tpl.${s.template}`) })} />;
}

export function useStartOrder() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (v: 'image' | 'video' | { kind: 'image' | 'video'; templateId: string }) =>
      api<{ token: string; order: Order }>('POST', '/api/orders', typeof v === 'string' ? { kind: v, lang } : { ...v, lang }),
    onSuccess: (r) => {
      storage.set('lastOrder', r.token);
      navigate(`/o/${r.token}`);
    },
    onError: (err) => toast.error(errText(t, err)),
  });
}

export default function Landing() {
  const { t, tl } = useT();
  const config = useConfig();
  const navigate = useNavigate();
  const trending = useTemplates({ featured: true });
  const [slide, setSlide] = useState(0);
  const last = storage.get('lastOrder');

  useEffect(() => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setSlide((i) => (i + 1) % SAMPLES.length), 3200);
    return () => clearInterval(id);
  }, []);

  const products = (config.data?.products ?? []).filter((p) => !p.upgrade);
  const videoFrom = Math.min(...products.filter((p) => p.kind === 'video').map((p) => p.price));

  const plan = (p: Product) => {
    const features = [...aiFeature(t, config.data, p), ...tl(`product.${p.id}.features`)];
    return (
      <article key={p.id} className={clsx('card flex flex-col p-6', p.popular && 'border-2 border-brand shadow-[0_20px_50px_-24px_rgb(228_87_46/0.55)]')}>
        <header className="flex min-h-6 items-center justify-between gap-2">
          <span className="text-[0.82rem] text-muted">{t('home.image')}</span>
          {p.popular && <span className="rounded-full bg-ok px-2.5 py-0.5 text-xs font-bold text-white">{t('pay.popular')}</span>}
        </header>
        <h3 className="mt-2 text-xl font-semibold">{t(`product.${p.id}.name`)}</h3>
        <p className="mt-0.5 mb-3.5 text-3xl font-bold tracking-tight">{formatTSh(p.price)}</p>
        <ul className="mb-5 grid flex-1 content-start gap-2 text-[0.92rem] text-ink-2">
          {features.map((f) => (
            <li key={f} className="flex gap-2.5">
              <Check className="mt-0.5 size-4 shrink-0 text-ok" aria-hidden />
              {f}
            </li>
          ))}
        </ul>
        <button className={clsx('btn w-full', p.popular ? 'btn-primary' : 'btn-secondary')} onClick={() => navigate('/create')}>
          {t('land.choose')}
        </button>
      </article>
    );
  };

  return (
    <>
      <section className="grid items-center gap-10 pt-10 pb-14 lg:grid-cols-[1.15fr_0.85fr] lg:pt-18 lg:pb-22">
        <div>
          <p className="eyebrow mb-3.5">{t('land.eyebrow')}</p>
          <h1 className="mb-4.5 text-[clamp(1.9rem,7vw,3.5rem)] leading-[1.03] font-bold tracking-[-0.04em]">
            {t('land.title_a')}{' '}
            <em className="bg-[linear-gradient(transparent_70%,rgb(228_87_46/0.16)_70%)] text-brand not-italic">{t('land.title_b')}</em>
          </h1>
          <p className="mb-6.5 max-w-[34em] text-lg text-ink-2">{t('land.lead')}</p>
          <div className="flex flex-wrap gap-2.5">
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/create')}>
              <Sparkles className="size-5" aria-hidden />
              {t('land.cta')}
            </button>
            <button className="btn btn-ghost btn-lg" onClick={() => navigate('/create?tab=video')}>
              <Play className="size-5" aria-hidden />
              {t('land.cta_video')}
            </button>
          </div>
          {last && (
            <p className="mt-4">
              <Link className="font-semibold text-brand-dark" to={`/o/${last}`}>
                {t('home.resume')} →
              </Link>
            </p>
          )}
          <ul className="mt-6.5 flex flex-wrap gap-x-5 gap-y-2 text-[0.88rem] text-muted">
            {[[Eye, 'land.trust_free'], [Smartphone, 'land.trust_pay'], [Globe, 'land.trust_lang']].map(([Icon, k]) => {
              const I = Icon as typeof Eye;
              return (
                <li key={k as string} className="flex items-center gap-2">
                  <I className="size-[18px] text-ok" aria-hidden />
                  {t(k as string)}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="relative grid justify-items-center" aria-hidden>
          <div className="absolute inset-x-[-10%] top-[8%] bottom-[14%] -z-10 rounded-full bg-[radial-gradient(closest-side,rgb(228_87_46/0.22),transparent),radial-gradient(closest-side_at_70%_30%,rgb(242_169_0/0.18),transparent)] blur-[10px]" />
          <div className="relative aspect-[9/16] w-[min(280px,72vw)] -rotate-2 rounded-[34px] bg-[#111] p-2.5 shadow-lift ring-2 ring-[#2a2a2e] ring-inset">
            <span className="absolute top-[18px] left-1/2 z-[2] h-1.5 w-[70px] -translate-x-1/2 rounded bg-[#2a2a2e]" />
            {SAMPLES.map((s, i) => (
              <SampleAd
                key={s.n}
                s={s}
                format="status"
                scale={0.4}
                className={clsx('absolute inset-2.5 h-[calc(100%-20px)] w-[calc(100%-20px)] rounded-[26px] bg-[#222] transition-opacity duration-700', i === slide ? 'opacity-100' : 'opacity-0')}
              />
            ))}
          </div>
          <div className="mt-5.5 flex gap-1.5">
            {SAMPLES.map((s, i) => (
              <span key={s.n} className={clsx('h-1.5 rounded-sm transition-all', i === slide ? 'w-5 bg-brand' : 'w-1.5 bg-[#d6d0c7]')} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-[calc(50%-50vw)] border-y border-line bg-white px-[max(16px,calc(50vw-544px))] py-14 lg:py-18">
        <h2 className="mb-7 text-[clamp(1.35rem,4.5vw,2.1rem)] leading-tight font-bold tracking-[-0.03em]">{t('land.styles_title')}</h2>
        <div className="grid auto-cols-[minmax(170px,1fr)] grid-flow-col gap-4 overflow-x-auto pb-3 [scrollbar-width:none] md:grid-flow-row md:grid-cols-5 md:overflow-visible">
          {SAMPLES.map((s) => (
            <figure key={s.n} className="group m-0 snap-start">
              <SampleAd s={s} format="post" scale={0.32} className="block aspect-[4/5] h-auto w-full rounded-[14px] bg-[#eee] shadow-card transition group-hover:-translate-y-1 group-hover:shadow-lift" />
              <figcaption className="mt-2.5 text-[0.95rem] font-semibold">{t(`tpl.${s.template}`)}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {!!trending.data?.length && (
        <section className="pt-14 lg:pt-20">
          <div className="mb-6 flex items-end justify-between gap-3">
            <h2 className="text-[clamp(1.35rem,4.5vw,2.1rem)] font-bold tracking-[-0.03em]">{t('land.templates_title')}</h2>
            <Link to="/templates" className="inline-flex items-center gap-1 font-semibold whitespace-nowrap text-brand-dark">{t('land.templates_all')}<ArrowRight className="size-4" /></Link>
          </div>
          <div className="grid auto-cols-[minmax(160px,1fr)] grid-flow-col gap-4 overflow-x-auto pb-3 [scrollbar-width:none] md:grid-flow-row md:grid-cols-4 md:overflow-visible">
            {trending.data.slice(0, 8).map((x) => <TemplateCard key={x.id} t={x} />)}
          </div>
        </section>
      )}

      <section className="py-14 lg:py-20">
        <h2 className="mb-7 text-[clamp(1.35rem,4.5vw,2.1rem)] font-bold tracking-[-0.03em]">{t('land.how_title')}</h2>
        <ol className="grid gap-4 md:grid-cols-3">
          {[Camera, Palette, Download].map((Icon, i) => (
            <li key={i} className="card p-6">
              <span className="mb-4 grid size-11 place-items-center rounded-[13px] bg-brand-soft text-brand-dark">
                <Icon className="size-[22px]" aria-hidden />
              </span>
              <h3 className="mb-1.5 text-lg font-semibold">{t(`land.step${i + 1}_t`)}</h3>
              <p className="text-muted">{t(`home.step${i + 1}`)}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="py-14 lg:py-20" id="prices">
        <h2 className="mb-2.5 text-[clamp(1.35rem,4.5vw,2.1rem)] font-bold tracking-[-0.03em]">{t('land.prices_title')}</h2>
        <p className="mb-7 text-ink-2">{t('land.prices_lead')}</p>
        <div className="grid items-stretch gap-4 md:grid-cols-3">{products.filter((p) => p.kind === 'image').map(plan)}</div>
        {Number.isFinite(videoFrom) && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-dashed border-[#d9d4cc] px-6 py-4.5 text-ink-2">
            <div className="flex items-center gap-3.5">
              <span className="grid size-11 place-items-center rounded-[13px] bg-brand-soft text-brand-dark">
                <Video className="size-[22px]" aria-hidden />
              </span>
              <span>
                <b>{t('home.video')}</b>
                <br />
                {t('home.video_desc')}
              </span>
            </div>
            <button className="btn btn-secondary" onClick={() => navigate('/create?tab=video')}>
              {t('home.from', { price: formatTSh(videoFrom) })}
            </button>
          </div>
        )}
      </section>

      <section className="max-w-[760px] py-14 lg:py-20">
        <h2 className="mb-5 text-[clamp(1.35rem,4.5vw,2.1rem)] font-bold tracking-[-0.03em]">{t('land.faq_title')}</h2>
        {[1, 2, 3, 4].map((n) => (
          <details key={n} className="group border-b border-line">
            <summary className="relative cursor-pointer list-none py-4.5 pr-9 text-[1.02rem] font-semibold [&::-webkit-details-marker]:hidden">
              {t(`land.faq${n}_q`)}
              <span aria-hidden className="absolute top-1/2 right-1 -translate-y-1/2 text-2xl font-normal text-muted transition group-open:rotate-45">+</span>
            </summary>
            <p className="mb-4.5 text-ink-2">{t(`land.faq${n}_a`)}</p>
          </details>
        ))}
      </section>

      <section className="mt-6 rounded-[28px] bg-[radial-gradient(120%_140%_at_0%_0%,#f08a4b_0%,#e4572e_45%,#a8361a_100%)] px-6 py-14 text-center text-white">
        <h2 className="mx-auto mb-6 max-w-[16em] text-[clamp(1.35rem,4.5vw,2.1rem)] leading-tight font-bold tracking-[-0.03em]">{t('land.final_title')}</h2>
        <button className="btn btn-light btn-lg" onClick={() => navigate('/create')}>
          {t('land.final_cta')}
          <ArrowRight className="size-5" aria-hidden />
        </button>
      </section>
    </>
  );
}
